"use client";

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import MarkdownDisplay from '@/components/Common/MarkdownDisplay';
import Modal from '@/components/Common/Modal';
import { CitationBadges } from '@/components/ChatInterface/CitationBadges';
import { parseAssistantContent, type AssistantMessageSection } from '@/lib/assistantMessageContent';
import type { Citation } from '@/services/rag';

interface ParsedAssistantMessageProps {
  content: string;
  className?: string;
  /** Retrieval sources for this answer, shown inside the details panel. */
  sources?: Citation[];
}

function sectionHeading(section: AssistantMessageSection, t: (key: string) => string): string {
  if (section.i18nKey) {
    return t(`aiChatbot.structured.${section.i18nKey}`);
  }
  return section.title;
}

// True when the string carries actual words/numbers, not just markdown punctuation
// (e.g. a stray "**" left over when the parser over-split a plain answer).
const hasMeaningfulText = (value: string): boolean => /[A-Za-zÀ-ÿ0-9]/.test(value);

const ParsedAssistantMessage: React.FC<ParsedAssistantMessageProps> = ({ content, className, sources }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const parsed = useMemo(() => parseAssistantContent(content), [content]);

  // Guard against a degenerate parse: when the extracted body has no real text
  // (the section splitter hijacked the whole answer into meta sections), fall
  // back to rendering the raw answer so the user never sees just "**".
  const bodyIsDegenerate = !hasMeaningfulText(parsed.body);
  const body = bodyIsDegenerate
    ? (hasMeaningfulText(content) ? content : t('aiChatbot.emptyAnswer'))
    : parsed.body;
  const metaSections = bodyIsDegenerate ? [] : parsed.metaSections;

  const messageSources = sources ?? [];
  const hasDetails = metaSections.length > 0 || messageSources.length > 0;

  return (
    <>
      <div className="relative">
        {hasDetails && (
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="absolute right-0 top-0 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
            aria-label={t('aiChatbot.showMessageDetails')}
            title={t('aiChatbot.showMessageDetails')}
          >
            <Info className="h-4 w-4" />
          </button>
        )}
        <MarkdownDisplay
          content={body}
          className={[className, hasDetails ? 'pr-8' : ''].filter(Boolean).join(' ')}
        />
      </div>

      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={t('aiChatbot.messageDetails')}
        className="max-w-2xl"
        bodyClassName="max-h-[70vh] overflow-y-auto"
      >
        <div className="space-y-5">
          {metaSections.map((section, index) => (
            <div key={`${section.title}-${index}`} className="space-y-2">
              <h4 className="text-sm font-semibold text-foreground">{sectionHeading(section, t)}</h4>
              <MarkdownDisplay content={section.content} className="text-sm" />
            </div>
          ))}
          {messageSources.length > 0 && <CitationBadges citations={messageSources} />}
        </div>
      </Modal>
    </>
  );
};

export default ParsedAssistantMessage;
